'use client'

import { useState } from 'react'
import { Dialog, DialogPanel } from '@headlessui/react'
import {
    Bars3Icon,
    XMarkIcon,
    ChatBubbleLeftRightIcon,
    UserGroupIcon,
    ShieldCheckIcon,
    GlobeAltIcon,
} from '@heroicons/react/24/outline'
import { Button } from './ui/button'

const navigation = [
    { name: 'About', href: '#about' },
    { name: 'FAQ', href: '#faq' },
    { name: 'Resources', href: '#resources' },
]

const features = [
    {
        name: 'Real-life scenarios',
        icon: ChatBubbleLeftRightIcon,
    },
    {
        name: 'Build confidence',
        icon: UserGroupIcon,
    },
    {
        name: "Stand up for what's right",
        icon: ShieldCheckIcon,
    },
    {
        name: 'Inclusive for all',
        icon: GlobeAltIcon,
    },
]

export default function Example() {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

    return (
<section
    className="relative border-b-[12px] border-black bg-cover bg-center bg-no-repeat"
    style={{ backgroundImage: "url('/assets/HeroBackground.png')" }}
>
            <header className="mt-20 border-b border-black/10">

                <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
                    <div className="fixed inset-0 z-50 bg-black/30" />
                    <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full max-w-sm overflow-y-auto bg-white px-6 py-6">
                        <div className="flex items-center justify-between">
                            <span className="text-lg font-black uppercase">Menu</span>
                            <button
                                type="button"
                                onClick={() => setMobileMenuOpen(false)}
                                className="rounded-md p-2 text-black"
                            >
                                <span className="sr-only">Close menu</span>
                                <XMarkIcon aria-hidden="true" className="size-6" />
                            </button>
                        </div>

                        <div className="mt-8 space-y-4">
                            {navigation.map((item) => (
                                <a
                                    key={item.name}
                                    href={item.href}
                                    className="block rounded-lg border border-black/10 px-3 py-3 text-base font-semibold text-black"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    {item.name}
                                </a>
                            ))}

                            <Button
                                size="lg"
                                className="comic-relief-bold mt-4 w-full border-2 border-black bg-blue-600 text-black shadow-[3px_3px_0_#000]"
                                variant="play"
                                onClick={() => {
                                    setMobileMenuOpen(false)
                                    document
                                        .getElementById('instructions')
                                        ?.scrollIntoView({ behavior: 'smooth' })
                                }}
                            >
                                Instructions
                            </Button>
                        </div>
                    </DialogPanel>
                </Dialog>
            </header>

            <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-10">
                <div className="grid items-center gap-8 lg:grid-cols-[0.82fr_1.18fr] lg:gap-10">
                    <div className="max-w-lg">
                        <div className="mb-2">
                            <img
                                src="/assets/CIOL_Title.png"
                                alt="Call It Out Loud"
                                className="w-full max-w-[520px] h-auto drop-shadow-[6px_6px_0_#000]"
                            />
                        </div>

                        <p
                            className="mt-4 flex flex-wrap items-center justify-between text-2xl sm:text-3xl text-black"
                            style={{
                                fontFamily: "'Bangers', cursive",
                                // textShadow: "2px 2px 0 #000",
                                letterSpacing: "2px",
                            }}
                        >
                            <span>Speak up.</span>
                            <span>Stand tall.</span>
                            <span>Create change.</span>
                        </p>

                        <p className="mt-3 max-w-md text-xl leading-8 text-black sm:text-xl">
                            Call It Out Loud is an interactive anti-bullying game that empowers young people to speak up, challenge harmful behavior, and build a culture of empathy and respect.
                        </p>

                        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                            <Button
                                size="lg"
                                className="comic-relief-bold min-w-[190px] border-2 border-black bg-red-500 text-white shadow-[4px_4px_0_#000]"
                                variant="play"
                                onClick={() => (window.location.href = '/play')}
                            >
                                Play Now! <span aria-hidden="true">→</span>
                            </Button>

                            <Button
                                size="lg"
                                className="comic-relief-bold min-w-[190px] border-2 border-black bg-white text-black shadow-[4px_4px_0_#000]"
                                variant="play"
                                onClick={() =>
                                    document.getElementById('about')?.scrollIntoView({
                                        behavior: 'smooth',
                                    })
                                }
                            >
                                Learn More <span aria-hidden="true">→</span>
                            </Button>
                        </div>
                    </div>

<div className="relative">
    <div className="relative aspect-[10/6] overflow-hidden border-[6px] border-black bg-black shadow-[6px_6px_0_#000] sm:min-h-[440px] sm:aspect-auto sm:shadow-[10px_10px_0_#000]">
        <div className="absolute inset-0">
            <img
                src="/assets/Panel1.png"
                alt=""
                className="absolute left-[2.5%] top-[3%] z-40 h-[39%] w-auto sm:left-[2.5%] sm:top-[2.5%] sm:h-auto sm:w-[50%]"
            />

            <img
                src="/assets/Panel2.png"
                alt=""
                className="absolute right-[2.5%] top-[3%] z-40 h-[39%] w-auto sm:right-[2.5%] sm:top-[2.5%] sm:h-auto sm:w-[50%]"
            />

            <img
                src="/assets/Panel3.png"
                alt=""
                className="absolute bottom-[7%] left-[2.5%] z-30 h-[52%] w-auto sm:bottom-[4%] sm:left-[2.5%] sm:h-auto sm:w-[35%]"
            />

            <img
                src="/assets/Panel4.png"
                alt=""
                className="absolute bottom-[4.5%] left-1/2 z-40 h-[46%] w-auto -translate-x-1/2 sm:bottom-[2.5%] sm:h-auto sm:w-[45%]"
            />

            <img
                src="/assets/Panel5.png"
                alt=""
                className="absolute bottom-[7%] right-[2.5%] z-30 h-[42%] w-auto sm:bottom-[4%] sm:right-[2.5%] sm:h-auto sm:w-[35%]"
            />
        </div>
    </div>
</div>
                </div>
            </div>

            <div className="relative border-t-0 border-yellow-600 bg-[#000]/90 backdrop-blur-[1px]">
                <div className="mx-auto grid max-w-7xl gap-3 px-4 py-4 sm:grid-cols-2 sm:px-6 lg:grid-cols-4">
                    {features.map((feature, index) => {
                        const Icon = feature.icon

                        const panelShapes = [
                            "polygon(8%_0%,100%_0%,92%_100%,0%_100%)",
                            "polygon(0%_0%,92%_0%,100%_100%,8%_100%)",
                            "polygon(6%_0%,100%_0%,94%_100%,0%_100%)",
                            "polygon(0%_0%,94%_0%,100%_100%,6%_100%)",
                        ]

                        return (
                            <div
                                key={feature.name}
                                className="relative"
                            >
                                {/* black outer panel border */}
                                <div
                                    className="bg-black p-[3px] shadow-[5px_5px_0_#000]"
                                    style={{
                                        clipPath: panelShapes[index % panelShapes.length],
                                    }}
                                >
                                    {/* inner panel */}
                                    <div
                                        className="flex items-center justify-center gap-2 bg-[#fff] px-3 py-2 text-center"
                                        style={{
                                            clipPath: panelShapes[index % panelShapes.length],
                                        }}
                                    >
                                        <Icon className="size-5 shrink-0 text-balck" />
                                        <span className="text-md font-bold text-balck lg:text-lg">
                                            {feature.name}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </section>
    )
}